import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { Loader2, Upload, AlertTriangle, Sparkles, FileText, MessageSquare, CheckCircle2, ChevronDown, X, ArrowLeft, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { CallMemberAnswers } from "@/components/admin/CallMemberAnswers";

import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type AnalysisStatus = "pending" | "analyzing" | "ready" | "error";

interface Analysis {
  id: string;
  call_number: string | null;
  call_title: string | null;
  call_date: string | null;
  status: AnalysisStatus;
  error_note: string | null;
  created_at: string;
}

interface Finding {
  id: string;
  analysis_id: string;
  kind: "new_topic" | "update_candidate" | "conflict_flag" | "covered" | "notable_moment";
  title: string;
  summary: string;
  detail: string | null;
  timestamp_ref: string | null;
  related_doc_ids: string[];
  suggested_collection: "faq" | "sop" | null;
  status: "open" | "accepted" | "dismissed";
  resolution_note: string | null;
}

const KIND_LABELS: Record<Finding["kind"], string> = {
  conflict_flag: "Conflict flags",
  new_topic: "New topics",
  update_candidate: "Update candidates",
  notable_moment: "Notable moments",
  covered: "Already covered",
};

const KIND_ORDER: Finding["kind"][] = ["conflict_flag", "new_topic", "update_candidate", "notable_moment", "covered"];

function StatusBadge({ status }: { status: AnalysisStatus }) {
  if (status === "pending") return <Badge variant="secondary">Queued</Badge>;
  if (status === "analyzing") return <Badge className="bg-blue-500/15 text-blue-700 border-blue-300"><Loader2 className="h-3 w-3 animate-spin mr-1" />Analyzing</Badge>;
  if (status === "ready") return <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-300">Ready</Badge>;
  return <Badge variant="destructive">Error</Badge>;
}

export default function AdminCallMining() {
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [showAccepted, setShowAccepted] = useState(true);
  const [showDismissed, setShowDismissed] = useState(false);

  // form
  const [callNumber, setCallNumber] = useState("");
  const [callTitle, setCallTitle] = useState("");
  const [callDate, setCallDate] = useState("");
  const [transcript, setTranscript] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // action state
  const [busyFindingId, setBusyFindingId] = useState<string | null>(null);
  const [noteById, setNoteById] = useState<Record<string, string>>({});
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadAnalyses();
    const channel = supabase
      .channel("call_analyses_changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "call_analyses" }, () => loadAnalyses())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  useEffect(() => {
    if (!selectedId) { setFindings([]); return; }
    loadFindings(selectedId);
    const channel = supabase
      .channel(`call_findings_${selectedId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "call_findings", filter: `analysis_id=eq.${selectedId}` },
        () => loadFindings(selectedId),
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [selectedId]);

  async function loadAnalyses() {
    const { data, error } = await supabase
      .from("call_analyses")
      .select("id, call_number, call_title, call_date, status, error_note, created_at")
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Could not load analyses. Refresh to try again.");
      setLoading(false);
      return;
    }
    setAnalyses((data as Analysis[]) || []);
    setLoading(false);
  }

  async function loadFindings(id: string) {
    setFindingsLoading(true);
    const { data, error } = await supabase
      .from("call_findings")
      .select("*")
      .eq("analysis_id", id)
      .order("created_at", { ascending: true });
    if (error) {
      toast.error("Could not load findings for this analysis.");
      setFindingsLoading(false);
      return;
    }
    setFindings((data as Finding[]) || []);
    setFindingsLoading(false);
  }

  function readFileToTranscript(file: File) {
    if (!file.name.toLowerCase().endsWith(".txt")) {
      toast.error("Only .txt files are supported here. Paste other formats directly.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      setTranscript((prev) => (prev ? prev + "\n\n" + text : text));
      toast.success("Transcript loaded from file.");
    };
    reader.onerror = () => toast.error("Could not read that file. Try pasting the text.");
    reader.readAsText(file);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!transcript.trim()) {
      toast.error("Add a transcript before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const insert = {
        call_number: callNumber.trim() || null,
        call_title: callTitle.trim() || null,
        call_date: callDate || null,
        transcript_text: transcript,
        status: "pending" as const,
        created_by: userData?.user?.id ?? null,
      };
      const { data: row, error } = await supabase
        .from("call_analyses")
        .insert(insert)
        .select("id")
        .single();
      if (error || !row) throw error || new Error("Insert failed");

      toast.success("Analysis queued. Working on it now.");
      setCallNumber(""); setCallTitle(""); setCallDate(""); setTranscript("");
      setSelectedId(row.id);
      await loadAnalyses();

      const { error: fnErr } = await supabase.functions.invoke("analyze-call", {
        body: { analysis_id: row.id },
      });
      if (fnErr) {
        console.error(fnErr);
        toast.error("The analysis run did not complete. See the row for details.");
      }
      await loadAnalyses();
      await loadFindings(row.id);
    } catch (err) {
      console.error(err);
      toast.error("Could not queue this analysis. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  }

  async function updateFindingStatus(finding: Finding, newStatus: "accepted" | "dismissed") {
    setBusyFindingId(finding.id);
    const note = noteById[finding.id]?.trim() || null;
    const { error } = await supabase
      .from("call_findings")
      .update({ status: newStatus, resolution_note: note })
      .eq("id", finding.id);
    setBusyFindingId(null);
    if (error) {
      toast.error("Could not save that. Try once more.");
      return;
    }
    if (newStatus === "accepted") {
      toast.success("Accepted. It stays here as the production brief.");
    } else {
      toast.success("Dismissed. It stays on the record.");
    }
    setNoteById((prev) => ({ ...prev, [finding.id]: "" }));
    loadFindings(finding.analysis_id);
  }

  async function reopenFinding(finding: Finding) {
    setBusyFindingId(finding.id);
    const { error } = await supabase
      .from("call_findings")
      .update({ status: "open", resolution_note: null })
      .eq("id", finding.id);
    setBusyFindingId(null);
    if (error) {
      toast.error("Could not reopen. Try again.");
      return;
    }
    toast.success("Reopened.");
    loadFindings(finding.analysis_id);
  }

  async function confirmDelete() {
    if (!deleteId) return;
    setDeleting(true);
    const { error } = await supabase.from("call_analyses").delete().eq("id", deleteId);
    setDeleting(false);
    if (error) {
      toast.error("Could not delete that analysis.");
      return;
    }
    toast.success("Analysis removed.");
    if (selectedId === deleteId) setSelectedId(null);
    setDeleteId(null);
    loadAnalyses();
  }

  const selected = analyses.find((a) => a.id === selectedId) || null;

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const f of findings) c[f.kind] = (c[f.kind] || 0) + 1;
    return c;
  }, [findings]);

  const grouped = useMemo(() => {
    const g: Record<string, Finding[]> = {};
    for (const k of KIND_ORDER) g[k] = [];
    for (const f of findings) {
      if (f.status === "accepted" && !showAccepted) continue;
      if (f.status === "dismissed" && !showDismissed) continue;
      if (!g[f.kind]) g[f.kind] = [];
      g[f.kind].push(f);
    }
    return g;
  }, [findings, showAccepted, showDismissed]);

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
          <Sparkles className="h-6 w-6" />
          Call Mining
        </h1>
        <p className="text-muted-foreground mt-1">
          Extract briefs from support call transcripts. It proposes. You dispose. Nothing here changes the library on its own.
        </p>
      </div>

      {!selected && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>New analysis</CardTitle>
              <CardDescription>Paste a transcript or upload a .txt file. All fields except the transcript are optional.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="callNumber">Call number</Label>
                    <Input id="callNumber" value={callNumber} onChange={(e) => setCallNumber(e.target.value)} placeholder="e.g. 142" />
                  </div>
                  <div>
                    <Label htmlFor="callTitle">Title</Label>
                    <Input id="callTitle" value={callTitle} onChange={(e) => setCallTitle(e.target.value)} placeholder="e.g. Weekly congregation, July 20" />
                  </div>
                  <div>
                    <Label htmlFor="callDate">Date</Label>
                    <Input id="callDate" type="date" value={callDate} onChange={(e) => setCallDate(e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="transcript">Transcript</Label>
                  <Textarea
                    id="transcript"
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    placeholder="Paste transcript here..."
                    className="min-h-[220px] font-mono text-xs"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer border rounded-md px-3 py-2 hover:bg-accent">
                    <Upload className="h-4 w-4" />
                    Upload .txt
                    <input
                      type="file"
                      accept=".txt,text/plain"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) readFileToTranscript(f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  <div className="text-xs text-muted-foreground">
                    {transcript.length.toLocaleString()} characters
                  </div>
                  <div className="ml-auto">
                    <Button type="submit" disabled={submitting || !transcript.trim()}>
                      {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Queueing</> : "Analyze"}
                    </Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Analyses</CardTitle>
              <CardDescription>Newest first. Select one to open the report.</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  <Skeleton className="h-14 w-full" />
                  <Skeleton className="h-14 w-full" />
                </div>
              ) : analyses.length === 0 ? (
                <div className="text-sm text-muted-foreground py-8 text-center">
                  No analyses yet. Paste a transcript above to start.
                </div>
              ) : (
                <div className="divide-y">
                  {analyses.map((a) => (
                    <div key={a.id} className="py-3 flex items-center gap-3">
                      <button
                        onClick={() => setSelectedId(a.id)}
                        className="flex-1 text-left hover:bg-accent -mx-2 px-2 py-1 rounded"
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">
                            {a.call_title || a.call_number || "Untitled call"}
                          </span>
                          <StatusBadge status={a.status} />
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {a.call_number && a.call_title ? `#${a.call_number} · ` : ""}
                          {a.call_date || new Date(a.created_at).toLocaleDateString()}
                          {a.status === "error" && a.error_note ? ` · ${a.error_note}` : ""}
                        </div>
                      </button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setDeleteId(a.id)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {selected && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setSelectedId(null)}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to list
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-semibold">
                  {selected.call_title || selected.call_number || "Untitled call"}
                </h2>
                <StatusBadge status={selected.status} />
              </div>
              <div className="text-xs text-muted-foreground">
                {selected.call_number ? `#${selected.call_number} · ` : ""}
                {selected.call_date || new Date(selected.created_at).toLocaleDateString()}
              </div>
            </div>
            <div className="flex gap-1 text-xs">
              <label className="inline-flex items-center gap-1">
                <input type="checkbox" checked={showAccepted} onChange={(e) => setShowAccepted(e.target.checked)} />
                Accepted
              </label>
              <label className="inline-flex items-center gap-1 ml-2">
                <input type="checkbox" checked={showDismissed} onChange={(e) => setShowDismissed(e.target.checked)} />
                Dismissed
              </label>
            </div>
          </div>

          {selected.status === "analyzing" && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertTitle>Working through the transcript</AlertTitle>
              <AlertDescription>Findings will appear here as soon as the pass finishes. This usually takes under a minute.</AlertDescription>
            </Alert>
          )}
          {selected.status === "error" && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>The analysis run did not complete</AlertTitle>
              <AlertDescription>{selected.error_note || "Try running it again in a moment."}</AlertDescription>
            </Alert>
          )}

          {selected.status === "ready" && (
            <>
              <CallMemberAnswers analysisId={selected.id} />


              <div className="flex flex-wrap gap-2 text-xs">
                {KIND_ORDER.map((k) => (
                  <Badge key={k} variant="secondary">{KIND_LABELS[k]}: {counts[k] || 0}</Badge>
                ))}
              </div>

              {findingsLoading ? (
                <Skeleton className="h-32 w-full" />
              ) : findings.length === 0 ? (
                <div className="text-sm text-muted-foreground py-8 text-center">
                  No findings surfaced from this transcript.
                </div>
              ) : (
                KIND_ORDER.map((kind) => {
                  const list = grouped[kind] || [];
                  if (list.length === 0) return null;
                  const isConflict = kind === "conflict_flag";
                  const isCovered = kind === "covered";
                  return (
                    <FindingSection
                      key={kind}
                      kind={kind}
                      list={list}
                      defaultOpen={!isCovered}
                      isConflict={isConflict}
                      isCovered={isCovered}
                      note={noteById}
                      setNote={setNoteById}
                      onAccept={(f) => updateFindingStatus(f, "accepted")}
                      onDismiss={(f) => updateFindingStatus(f, "dismissed")}
                      onReopen={reopenFinding}
                      busyId={busyFindingId}
                    />
                  );
                })
              )}
            </>
          )}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this analysis?</AlertDialogTitle>
            <AlertDialogDescription>
              The transcript and all findings will be removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting}>
              {deleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting</> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FindingSection({
  kind, list, defaultOpen, isConflict, isCovered, note, setNote, onAccept, onDismiss, onReopen, busyId,
}: {
  kind: Finding["kind"];
  list: Finding[];
  defaultOpen: boolean;
  isConflict: boolean;
  isCovered: boolean;
  note: Record<string, string>;
  setNote: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onAccept: (f: Finding) => void;
  onDismiss: (f: Finding) => void;
  onReopen: (f: Finding) => void;
  busyId: string | null;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className={isConflict ? "border-amber-400 bg-amber-50/40" : ""}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button className="w-full text-left px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isConflict ? <AlertTriangle className="h-5 w-5 text-amber-600" /> : <FileText className="h-5 w-5 text-muted-foreground" />}
              <span className="font-semibold">{KIND_LABELS[kind]}</span>
              <Badge variant="secondary">{list.length}</Badge>
            </div>
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-3">
            {isConflict && (
              <div className="text-sm text-amber-900 bg-amber-100/60 border border-amber-300 rounded-md px-3 py-2">
                Review against canon before anything changes. Conflicts never auto-apply.
              </div>
            )}
            {isCovered && (
              <div className="text-sm text-muted-foreground">
                These confirm the library already answers what members ask.
              </div>
            )}
            {list.map((f) => (
              <FindingCard
                key={f.id}
                f={f}
                note={note[f.id] || ""}
                setNote={(v) => setNote((p) => ({ ...p, [f.id]: v }))}
                onAccept={() => onAccept(f)}
                onDismiss={() => onDismiss(f)}
                onReopen={() => onReopen(f)}
                busy={busyId === f.id}
              />
            ))}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

function FindingCard({
  f, note, setNote, onAccept, onDismiss, onReopen, busy,
}: {
  f: Finding;
  note: string;
  setNote: (v: string) => void;
  onAccept: () => void;
  onDismiss: () => void;
  onReopen: () => void;
  busy: boolean;
}) {
  const closed = f.status !== "open";
  return (
    <div className={`border rounded-md p-3 space-y-2 ${closed ? "opacity-60 bg-muted/30" : "bg-background"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium">{f.title}</div>
            {f.status === "accepted" && <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-300"><CheckCircle2 className="h-3 w-3 mr-1" />Accepted</Badge>}
            {f.status === "dismissed" && <Badge variant="secondary"><X className="h-3 w-3 mr-1" />Dismissed</Badge>}
            {f.suggested_collection && (
              <Badge variant="outline" className="uppercase text-[10px]">{f.suggested_collection}</Badge>
            )}
            {f.timestamp_ref && <span className="text-xs text-muted-foreground">@ {f.timestamp_ref}</span>}
          </div>
          <p className="text-sm mt-1">{f.summary}</p>
          {f.detail && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{f.detail}</p>}
          {f.related_doc_ids.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {f.related_doc_ids.map((id) => {
                const isFaq = id.toUpperCase().startsWith("FAQ");
                const href = isFaq ? `/support/library/${id}` : `/support/library/${id}`;
                return (
                  <Link
                    key={id}
                    to={href}
                    className="text-xs px-2 py-0.5 rounded border hover:bg-accent"
                  >
                    {id}
                  </Link>
                );
              })}
            </div>
          )}
          {closed && f.resolution_note && (
            <div className="text-xs italic text-muted-foreground mt-2">Note: {f.resolution_note}</div>
          )}
        </div>
      </div>
      {!closed && (
        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          <Input
            placeholder="Optional note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="text-xs h-8"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={onAccept} disabled={busy}>
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Accept"}
            </Button>
            <Button size="sm" variant="outline" onClick={onDismiss} disabled={busy}>Dismiss</Button>
          </div>
        </div>
      )}
      {closed && (
        <div className="pt-1">
          <Button size="sm" variant="ghost" onClick={onReopen} disabled={busy}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Reopen"}
          </Button>
        </div>
      )}
    </div>
  );
}
